import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Languages,
  Sparkles,
  Globe2,
  Apple,
  ArrowRight,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Reveal } from "@/components/Reveal";

const SHOTS = [
  { src: `${process.env.PUBLIC_URL || ""}/welcome.webp`, label: "Welcome" },
  { src: "https://world-connect-dates.preview.emergentagent.com/shots/03_translation.png", label: "Live translation" },
  { src: "https://world-connect-dates.preview.emergentagent.com/shots/04_openers.png", label: "AI openers" },
];

const VALUE_PROPS = [
  {
    icon: BadgeCheck,
    title: "Verified profiles",
    desc: "Selfie verification keeps out bots and catfish. Every verified badge is a real, checked human.",
  },
  {
    icon: Languages,
    title: "Real-time chat translation",
    desc: "Message anyone, anywhere. Conversations translate instantly so language is never a barrier.",
  },
  {
    icon: Sparkles,
    title: "Smart conversation starters",
    desc: "Personalized opener suggestions for each match help you begin every chat naturally and confidently.",
  },
  {
    icon: Globe2,
    title: "Global matching",
    desc: "Match with people nearby or open it up worldwide. Love isn't limited by a postcode.",
  },
];

function PhoneMock({ src, label, index }) {
  const [broken, setBroken] = useState(false);
  const rotate = ["-rotate-3", "rotate-0", "rotate-3"][index] || "rotate-0";
  const lift = index === 1 ? "sm:-mt-6 z-10" : "sm:mt-5";
  return (
    <div
      className={`relative ${rotate} ${lift} transition-transform duration-300 hover:rotate-0 hover:-translate-y-2`}
      data-testid={`phone-mock-${index}`}
    >
      <div className="w-[210px] sm:w-[150px] lg:w-[172px] rounded-[2rem] p-2 bg-[hsl(189_60%_8%)] ring-1 ring-white/10 shadow-2xl shadow-black/40">
        <div className="aspect-[9/19] rounded-[1.6rem] overflow-hidden bg-[hsl(189_70%_14%)] grid place-items-center">
          {broken ? (
            <div className="flex flex-col items-center text-center px-4 text-muted-foreground">
              <Logo size={40} />
              <span className="text-xs mt-3">{label}</span>
              <span className="text-[10px] mt-1 opacity-70">Preview coming soon</span>
            </div>
          ) : (
            <img
              src={src}
              alt={`Amoreska — ${label}`}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => setBroken(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StoreBadge({ store, place = "hero" }) {
  const isApple = store === "apple";
  return (
    <div className="relative group" data-testid={`badge-${store}-${place}`}>
      <button
        type="button"
        disabled
        aria-label={isApple ? "Download on the App Store — coming soon" : "Get it on Google Play — coming soon"}
        className="flex items-center gap-3 rounded-xl bg-black/90 ring-1 ring-white/15 px-4 py-2.5 text-left cursor-not-allowed opacity-90"
      >
        {isApple ? (
          <Apple className="h-7 w-7 text-white" fill="currentColor" />
        ) : (
          <svg viewBox="0 0 512 512" className="h-6 w-6" aria-hidden="true">
            <path fill="#00D3FF" d="M47 24 285 262 47 500a28 28 0 0 1-15-25V49c0-11 6-20 15-25z" />
            <path fill="#00F076" d="M47 24c5-3 11-3 17 1l204 116-56 56z" />
            <path fill="#FFCE00" d="M373 197l-61 65 61 65 78-44c22-13 22-42 0-55z" />
            <path fill="#FF3D44" d="M212 318l56-56 105 105-104 59c-6 4-12 4-17 1z" />
          </svg>
        )}
        <span className="text-white leading-tight">
          <span className="block text-[10px] opacity-80">
            {isApple ? "Download on the" : "GET IT ON"}
          </span>
          <span className="block text-lg font-semibold -mt-0.5 font-display">
            {isApple ? "App Store" : "Google Play"}
          </span>
        </span>
      </button>
      <span className="absolute -top-2 -right-2 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 shadow-lg">
        Coming soon
      </span>
    </div>
  );
}

export default function Landing() {
  useEffect(() => {
    document.title = "Amoreska — Real connections. Everywhere.";
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <Logo size={38} />
            <span className="font-display text-xl tracking-tight">Amoreska</span>
          </a>
          <nav className="hidden sm:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#download" className="hover:text-foreground transition-colors">Download</a>
          </nav>
          <a
            href="#download"
            className="rounded-full bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 hover:opacity-90 transition-opacity"
            data-testid="nav-get-app"
          >
            Get the app
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/4 h-[28rem] w-[28rem] rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute top-40 -right-24 h-96 w-96 rounded-full bg-[hsl(173_45%_40%)]/15 blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-8 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center">
          <Reveal className="relative z-10 lg:pr-4" y={28}>
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 ring-1 ring-border/60 px-3 py-1 text-xs text-muted-foreground mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Now inviting members worldwide
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-xl" data-testid="hero-headline">
              Meet real people, <span className="text-primary">anywhere</span> in the world.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed" data-testid="hero-subhead">
              The global dating app built on trust — verified profiles, real-time chat translation,
              and matches that actually last.
            </p>

            <div className="flex flex-wrap gap-4 mt-9" data-testid="hero-badges">
              <StoreBadge store="apple" place="hero" />
              <StoreBadge store="google" place="hero" />
            </div>
            <p className="text-xs text-muted-foreground mt-4 flex items-center gap-1.5">
              <ArrowRight className="h-3.5 w-3.5" /> Free to download · Verified community
            </p>
          </Reveal>

          {/* Phones */}
          <div className="flex justify-center lg:justify-end gap-2 sm:gap-3 min-w-0" data-testid="hero-phones">
            {SHOTS.map((s, i) => (
              <Reveal
                key={s.label}
                delay={150 + i * 140}
                y={36}
                className={i > 0 ? "hidden sm:block" : ""}
              >
                <PhoneMock src={s.src} label={s.label} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Value props */}
      <section id="features" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Sticky intro */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <Reveal y={20}>
                <span className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
                  Why Amoreska
                </span>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-4 leading-[1.1]">
                  Dating that respects your time — and your safety.
                </h2>
                <p className="text-muted-foreground mt-5 leading-relaxed">
                  Everything about Amoreska is designed to help genuine people find each other faster.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Editorial feature list */}
          <div className="lg:col-span-8" data-testid="value-props">
            {VALUE_PROPS.map((v, i) => (
              <Reveal
                key={v.title}
                delay={i * 110}
                y={26}
                data-testid={`value-${v.title.split(" ")[0].toLowerCase()}`}
                className="group grid grid-cols-[auto_1fr] gap-5 sm:gap-8 py-8 border-t border-border/40"
              >
                <span className="font-display text-2xl sm:text-3xl tabular-nums text-muted-foreground/40 group-hover:text-primary transition-colors duration-300 leading-none pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="transition-transform duration-300 group-hover:translate-x-1">
                  <div className="flex items-center gap-3">
                    <v.icon className="h-5 w-5 text-primary shrink-0" strokeWidth={1.75} />
                    <h3 className="font-display text-xl sm:text-2xl tracking-tight">{v.title}</h3>
                  </div>
                  <p className="text-muted-foreground mt-3 leading-relaxed max-w-xl">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section id="download" className="max-w-6xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <Reveal y={32} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[hsl(189_78%_16%)] to-[hsl(189_82%_10%)] ring-1 ring-border/60 px-6 sm:px-14 py-14 sm:py-16 text-center">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-64 w-64 rounded-full bg-primary/25 blur-3xl" />
          <div className="relative">
            <Logo size={64} rounded="rounded-2xl" className="mx-auto" />
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-6">
              Your next real connection is one tap away.
            </h2>
            <p className="text-muted-foreground mt-4 max-w-lg mx-auto">
              Amoreska is launching on iOS and Android soon. Grab it the moment it drops.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <StoreBadge store="apple" place="cta" />
              <StoreBadge store="google" place="cta" />
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <Logo size={32} />
            <span className="font-display text-lg">Amoreska</span>
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground" data-testid="footer-links">
            <Link to="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms">Terms</Link>
            <Link to="/support" className="hover:text-foreground transition-colors" data-testid="footer-support">Support</Link>
            <a href="mailto:support@amoreska.app" className="hover:text-foreground transition-colors" data-testid="footer-contact">support@amoreska.app</a>
          </nav>
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-8 text-xs text-muted-foreground">
          © 2026 Amoreska.
        </div>
      </footer>
    </div>
  );
}
